import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller } from "react-hook-form";
import { Button, TextField } from '@mui/material';

import TemplateCreateRTEditor from '@/views/pages/Template/elements/TemplateCreateRTEditor';
import TemplateUseFormCalendar from '@/views/pages/Template/elements/TemplateUseFormCalendar';

import api from '@/services/api';
import { appActions } from "@/store/actions/app.actions";
import Alert from '@/views/components/Alert/Alert';

import { ModalContext } from '@/utils/contexts/ModalContext';

import styles from '@/views/pages/Template/Template.module.scss';

const FormFactory = (item, control, dict) => {
	// single line input:
	if (item.type.htmlType === 'input') return (
		<div key={ item.type.code }>
			<label className={ `${styles['form__label']}` }>{item.type.label}</label>
			<Controller
				render={ ({ field }) => <TextField 
					size='small'
					fullWidth
					style={ { backgroundColor: 'white' } }
					{ ...field } 
				/> }
				name={ item.type.code }
				control={ control }
			/>
		</div>
	);

	// multiline: 
	if (item.type.htmlType === 'textarea') return (
		<div key={ item.type.code }>
			<label className={ `${styles['form__label']}` }>{item.type.label}</label>
			<Controller
				render={ ({ field }) => <TextField 
					className='multiline-short'
					multiline
					rows={ 4 }
					size='small'
					fullWidth
					{ ...field } 
				/> }
				name={ item.type.code }
				control={ control }
			/>
		</div>
	);

	// date picker:
	if (item.type.htmlType === 'date') return (
		<div key={ item.type.code }>
			<label className={ `${styles['form__label']}` }>{item.type.label}</label>
			<Controller
				name={ item.type.code }
				control={ control }
				render={ ({ field }) => {
					return (
						<TemplateUseFormCalendar
							onChange={ dat => field.onChange(dat) }
						/>
					);
				} }
			/>

		</div>
	);

	// RichTextEditor (returns html)
	if (item.type.htmlType === 'html') return (
		<div key={ item.type.code }>
			<label className={ `${styles['form__label']}` }>{item.type.label}</label>
			{item.type.code === 'content-letter-text' && <div className={ `${styles['template-use-form__metrics__comment']}` }>
				{dict.templates.use.dontQuote}
			</div>}
			<TemplateCreateRTEditor
				name={ item.type.code }
				control={ control }
				cutted={ false }
			/>
		</div>
	);

	// RichTextEditor (returns html) with cutted down functionality
	if (item.type.htmlType === 'footnote-html') return (
		<div key={ item.type.code }>
			<label className={ `${styles['form__label']}` }>{item.type.label}</label>
			<TemplateCreateRTEditor
				name={ item.type.code }
				control={ control }
				cutted={ true }
			/>
		</div>
	);
};

const TemplateUseForm = ({ formInitialValues, entireData, uploadTemplateData }) => {
	const { dict, letterTemplatesSettings } = useSelector((state) => state.app);
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const { showModal } = useContext(ModalContext);

	const { control, handleSubmit } = useForm({ defaultValues: formInitialValues });

	const onSubmit = async (data) => {
		uploadTemplateData(data);
	};

	useEffect(() => {
		api.get('/letter-templates/settings').then((response) => {
			dispatch(appActions.setLetterTemplatesSettings(response.data));
		});
	}, []);
	
	return (
		<>
			<Alert
				style={ { marginBottom: 0 } }
				variant='info'
				alertContent={ <><strong>{dict.templates.use.warn1}</strong> { dict.templates.use.warn2 }</> }
			/>
			{ entireData && entireData.version === 'update-required' &&  <Alert
				style={ { marginBottom: 0 } }
				variant='warning'
				alertContent={ <><strong>{ dict.templates.create.warnUpdated.part1 }</strong>{ dict.templates.create.warnUpdated.part2 }</> }
			/> }
			<div className={ styles['template-use-form--wrapper'] }>
				<form onSubmit={ handleSubmit(onSubmit) }>
					<div className={ styles['template-use-form'] }>
						{/* Metrics */}
						<section 
							className={ styles['template-use-form__metrics'] }
							style={ { flex: 1 } }
						>
							<div className={ `${styles['template-use-form__metrics__title']}` }>
								{dict.templates.create.metricHeader}
							</div>
							<div className={ `${styles['template-use-form__metrics__comment']}` }>
								{entireData.metricFields.length > 0 && dict.templates.use.metricDescription}
								{entireData.metricFields.length === 0 && dict.templates.use.noMetricsDescription}
							</div>
							{entireData.metricFields.map(item => FormFactory(item, control, dict))}
						</section>

						{/* Letter content */}
						<section 
							className={ styles['template-use-form__letter-content'] }
							style={ { flex: 3 } }
						>
							<div className={ `${styles['template-use-form__letter-content__title']}` }>
								{dict.templates.create.letterContentTitle}
							</div>
							<div className={ `${styles['template-use-form__metrics__comment']}` }>
								{dict.templates.use.fillNeededDataComment}
								<span 
									className={ `${styles['template-use-form__metrics__comment--link']}` }
									onClick={ () => {
										showModal({
											componentName: 'ModalHowToCreateSimpleTexts',
										});
									} }
								>
									{dict.templates.use.fillNeededDataLink}
								</span>
							</div>
							{entireData.contentFields.map(item => FormFactory(item, control, dict))}
						</section>
					</div>
					<section className={ styles['template-use-form__buttons__section'] }>
						{letterTemplatesSettings && <div className={ styles['template-use-form__buttons__comment'] }>
							<div className={ styles['template-use-form__buttons__comment--icon'] }>
								<svg 
									width={ 20 }  
									height={ 21 } 
									viewBox={ `0 0 ${ 20 } ${ 21 }` }
									xmlns="http://www.w3.org/2000/svg"
								>
									<path 
										d="M8 0.5C3.58166 0.5 0 4.08178 0 8.5C0 12.9185 3.58166 16.5 8 16.5C12.4183 16.5 16 12.9185 16 8.5C16 4.08178 12.4183 0.5 8 0.5ZM8 4.5C8.55231 4.5 9 4.94775 9 5.5C9 6.0525 8.55231 6.5 8 6.5C7.44769 6.5 7 6.0525 7 5.5C7 4.94775 7.44769 4.5 8 4.5ZM9.5 12.5H6.5C6.22388 12.5 6 12.2764 6 12C6 11.7239 6.22388 11.5 6.5 11.5H7V8.5H6.5C6.22388 8.5 6 8.27637 6 8C6 7.72388 6.22388 7.5 6.5 7.5H8.5C8.77612 7.5 9 7.72388 9 8V11.5H9.5C9.77612 11.5 10 11.7239 10 12C10 12.2764 9.77612 12.5 9.5 12.5Z" 
										fill="#01366F"
									/>
								</svg>
							</div>
							<div>
								{dict.templates.use.downloadInfo.part1}
								{letterTemplatesSettings.fontFamily}
								{dict.templates.use.downloadInfo.part2}
								{letterTemplatesSettings.titleFontSize}
								{dict.templates.use.downloadInfo.part3}
								{letterTemplatesSettings.headerFontSize}
								{dict.templates.use.downloadInfo.part4}
								{letterTemplatesSettings.textFontSize}
								{dict.templates.use.downloadInfo.part5}
							</div>
						</div> }
						<div className={ styles['template-use-form__buttons__content'] }>
							<Button
								color='primary'
								variant='contained'
								className='button__transparent'
								sx={ { textTransform: 'none' } }
								onClick={ () => navigate(-1) }
							>
								{dict.cancel}
							</Button>
							<Button
								color='primary'
								type='submit'
								variant='contained'
								fullWidth
								className='button__blue'
								sx={ { textTransform: 'none' } }
							>
								{dict.templates.createLetterUponTemplate}
							</Button>
						</div>
					</section>
				</form>
			</div>
		</>
	);

};

export default React.memo(TemplateUseForm);